import React, { useEffect } from 'react';
import { Link, useParams, Navigate } from 'react-router-dom';
import { ChevronRight, Clock, Phone } from 'lucide-react';
import Seo from '@/components/Seo';
import Footer from '@/components/Footer';
import { blogArticles } from '@/data/blogArticles';
import { landingPages } from '@/data/landingPages';

const BASE_URL = 'https://taxiyellowcabcorsica.fr';
const PHONE = '+33674626368';
const PHONE_DISPLAY = '+33 6 74 62 63 68';

const formatDate = (d) => new Date(d).toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' });

const buildJsonLd = (article) => [
  {
    "@context": "https://schema.org",
    "@type": "Article",
    "headline": article.title,
    "description": article.metaDescription,
    "datePublished": article.date,
    "dateModified": article.date,
    "inLanguage": "fr-FR",
    "mainEntityOfPage": `${BASE_URL}/blog/${article.slug}`,
    "author": { "@type": "Organization", "name": "Taxis Yellow Cab Corsica", "url": BASE_URL },
    "publisher": { "@type": "Organization", "name": "Taxis Yellow Cab Corsica", "url": BASE_URL }
  },
  {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": [
      { "@type": "ListItem", "position": 1, "name": "Accueil", "item": BASE_URL },
      { "@type": "ListItem", "position": 2, "name": "Blog", "item": `${BASE_URL}/blog` },
      { "@type": "ListItem", "position": 3, "name": article.title, "item": `${BASE_URL}/blog/${article.slug}` }
    ]
  }
];

const BlogArticle = () => {
  const { slug } = useParams();
  const article = blogArticles.find((a) => a.slug === slug);

  useEffect(() => { window.scrollTo(0, 0); }, [slug]);

  if (!article) return <Navigate to="/blog" replace />;

  const related = article.relatedLanding.map((s) => landingPages[s]);

  return (
    <main className="bg-[#0A0A0A]" data-testid={`blog-article-${article.slug}`}>
      <Seo
        title={article.metaTitle}
        description={article.metaDescription}
        canonical={`${BASE_URL}/blog/${article.slug}`}
        jsonLd={buildJsonLd(article)}
      />

      <article className="pt-14 px-4">
        <div className="max-w-3xl mx-auto">
          <nav className="flex items-center gap-1.5 text-xs text-[#555] mb-8 flex-wrap" aria-label="Fil d'ariane">
            <Link to="/" className="hover:text-[#A3A3A3] transition-colors">Accueil</Link>
            <ChevronRight size={12} />
            <Link to="/blog" className="hover:text-[#A3A3A3] transition-colors">Blog</Link>
            <ChevronRight size={12} />
            <span className="text-[#A3A3A3] truncate max-w-[200px]">{article.title}</span>
          </nav>

          <div className="flex items-center gap-3 text-xs text-[#555] mb-4">
            <span>{formatDate(article.date)}</span>
            <span className="flex items-center gap-1"><Clock size={11} /> {article.readTime}</span>
          </div>
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-light text-white tracking-tight mb-10" style={{ fontFamily: 'Outfit, sans-serif' }}>
            {article.title}
          </h1>

          {article.sections.map((s) => (
            <section key={s.h2} className="mb-10">
              <h2 className="text-xl sm:text-2xl font-medium text-white mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>
                {s.h2}
              </h2>
              <div className="space-y-4">
                {s.paragraphs.map((p, i) => (
                  <p key={i} className="text-[#A3A3A3] text-base leading-relaxed">{p}</p>
                ))}
              </div>
            </section>
          ))}

          {/* CTA */}
          <div className="bg-[#1A1A1A] border border-[#EAB308]/20 rounded-2xl p-8 text-center mb-12">
            <h3 className="text-xl font-medium text-white mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>{article.ctaTitle}</h3>
            <p className="text-[#A3A3A3] text-sm mb-5">Disponible 24h/24 · Tarif fixe convenu à l'avance · Réponse sous 30 min</p>
            <div className="flex flex-wrap justify-center gap-3">
              <a href={`tel:${PHONE}`} data-testid="article-call-btn"
                className="inline-flex items-center gap-2 bg-[#EAB308] hover:bg-[#CA8A04] text-black font-bold px-6 py-3 rounded-full text-sm transition-colors">
                <Phone size={15} /> {PHONE_DISPLAY}
              </a>
              <Link to="/#reservation" data-testid="article-book-btn"
                className="inline-flex items-center gap-2 border border-white/15 hover:border-[#EAB308]/40 text-white px-6 py-3 rounded-full text-sm font-medium transition-all">
                Réserver en ligne
              </Link>
            </div>
          </div>

          {/* Related landing pages */}
          <div className="mb-16">
            <h3 className="text-sm font-semibold text-white mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>À lire aussi</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {related.map((r) => (
                <Link key={r.slug} to={`/${r.slug}`}
                  className="bg-[#1A1A1A] border border-white/5 hover:border-[#EAB308]/30 rounded-xl p-5 group transition-all">
                  <p className="text-white text-sm font-medium group-hover:text-[#EAB308] transition-colors">{r.breadcrumb}</p>
                  <p className="text-[#555] text-xs mt-1">{r.badge}</p>
                </Link>
              ))}
            </div>
          </div>
        </div>
      </article>

      <Footer />
      <div className="h-16 md:h-14" aria-hidden="true" />
    </main>
  );
};

export default BlogArticle;
